/**
 * MultiInput component module.
 * @module shared/components/MultiInput
 *
 */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import styled, {withTheme} from 'styled-components/macro';
import clone from 'clone';
import {Column, Icon, RadioOption, Row} from '../../shared';
import {fill, layoutAttribute} from './cssUtils';
import TitleLabel from './TitleLabel';
import {v4 as uuid} from 'uuid';

const _Inline = styled.div`
  display: inline-block;
`;

const _PlusIconWrapper = styled.div`
  width: ${fill(null)};
  text-align: center;
`;

/**
 * MultiInput component
 * @name MultiInput
 */
const MultiInput = props => {
  const {
    InputComponent,
    dataPoint,
    onChange,
    componentProps,
    dataPropName,
    emptyValue,
    initialValue,
    width,
    margin,
    hasPrimary,
    required,
    layout,
    readOnly,
    title,
    displayHeaderTitle,
    maxInputs,
  } = props;

  const [inputDataPoints, setInputDataPoints] = useState();
  const [inputEmptyValue, setInputEmptyValue] = useState();
  const [keyMod, setKeyMod] = useState({mod: uuid(), enabled: false});

  const [radioOptions, setRadioOptions] = useState(hasPrimary
    ? dataPoint.value && dataPoint.value.map(dp => !!dp.primary)
    : null);

  const copyProps = val => {
    if (dataPropName) {
      return val[dataPropName];
    }
    return val;
  };

  useEffect(() => {
    if (!dataPoint.value || !dataPoint.value.length) {
      onChange(dataPoint.name, [clone(emptyValue)]);
      return;
    }

    if (hasPrimary && dataPoint.value.every(d => !d.primary)) {
      const data = [...dataPoint.value];
      data[0].primary = true;

      onChange(dataPoint.name, data);
      return;
    }

    const data = dataPoint.value.map((val, idx) => (
      {
        name: String(idx),
        value: copyProps(val),
      }
    ));

    setInputDataPoints(data);
  }, [dataPoint]);

  useEffect(() => {
    if (hasPrimary && dataPoint.value) {
      let newRadioOptions = dataPoint.value.map(dp => !!dp.primary);

      if (newRadioOptions.every(radio => !radio)) {
        newRadioOptions[0] = true;
      }

      setRadioOptions(newRadioOptions);
    }

    setKeyMod({...keyMod, enabled: true});
  }, [inputDataPoints]);

  useEffect(() => {
    setInputEmptyValue(copyProps(emptyValue));
  }, [emptyValue]);

  const radioOptionOnChange = (name) => {
    const newRadioOptions = radioOptions.map((radio, idx) => (idx === Number(name)));
    setRadioOptions(newRadioOptions);

    const newData = dataPoint.value.map((dp, idx) => ({...dp, primary: (idx === Number(name))}));
    onChange(dataPoint.name, newData);
  };

  const addInput = () => {
    if (!dataPoint.value) {return;}

    const initialVal = (initialValue !== undefined) ? clone(initialValue) : clone(emptyValue);

    if (hasPrimary) {
      initialVal.primary = false;

      const newRadioOptions = [...radioOptions];
      newRadioOptions.push(false);

      setRadioOptions(newRadioOptions);
    }

    const newData = [...dataPoint.value];
    newData.push(initialVal);

    onChange(dataPoint.name, newData);
  };

  const removeInputWrapper = idx => (
    () => {
      if (!dataPoint.value) {return;}

      if (dataPoint.value.length > 1) {
        let newData;

        if (hasPrimary && radioOptions[idx]) {
          const newRadioOptions = [...radioOptions];
          newRadioOptions[0] = true;

          setRadioOptions(newRadioOptions);

          newData = dataPoint.value.map((dp, i) => ({...dp, primary: !i}));
        } else {
          newData = [...dataPoint.value];
        }

        newData.splice(idx, 1);

        setKeyMod({mod: uuid(), enabled: false});
        onChange(dataPoint.name, newData);
      } else if (dataPoint.value.length === 1) {
        if (hasPrimary) {
          setRadioOptions([true]);
        }

        setKeyMod({mod: uuid(), enabled: false});
        onChange(dataPoint.name, [clone(emptyValue)]);
      }
    }
  );

  const subInputOnChange = (name, value) => {
    if (!dataPoint.value) {return;}

    const newData = [...dataPoint.value];
    const idx = Number(name);

    let newDatum = {...newData[idx]};

    if (dataPropName) {
      newDatum[dataPropName] = clone(value);
    } else {
      newDatum = value;
    }

    newData[idx] = hasPrimary ? {...newDatum, primary: Number(radioOptions[idx])} : newDatum;

    onChange(dataPoint.name, newData);
  };

  let inputs;

  if (inputDataPoints && inputDataPoints.length) {
    inputs = inputDataPoints.map((dp, idx) => {
      const removeInput = removeInputWrapper(idx);

      let radioOption = null;

      if (hasPrimary) {
        radioOption = (
          <_Inline>
            <RadioOption
              dataPoint={{name: String(idx), value: radioOptions[idx]}}
              selected={radioOptions[idx]}
              disabled={!!readOnly}
              readOnly={!!readOnly}
              onChange={radioOptionOnChange}
            />
          </_Inline>
        );
      }

      return (
        <Row
          key={idx + (keyMod.enabled ? keyMod.mod : 0)}
          height={'fit-content'}
          justifySubRow={'left'}
          alignItems={'middle'}
          wrap={false}
        >
          {radioOption}
          <InputComponent
            {...componentProps}
            {...(displayHeaderTitle && idx > 0 && {title: undefined})}
            readOnly={!!readOnly}
            dataPoint={{...dp}}
            onChange={subInputOnChange}
            layout={{grow: 1}}
            required={required || ((hasPrimary && radioOptions && (radioOptions.length > 1)) ? radioOptions[idx] : false)}
            width={!hasPrimary ? '90%' : '80%'}
          />
          {!readOnly && <Icon svg={'remove'} action={removeInput} disabled={!!readOnly}/>}
        </Row>
      );
    });
  } else {
    let radioOption = null;

    if (hasPrimary) {
      radioOption = <_Inline>
        <RadioOption
          dataPoint={{name: '0', value: true}}
          selected={true}
          disabled={!!readOnly}
          onChange={radioOptionOnChange}
        />
      </_Inline>;
    }

    inputs = <Row
      justifySubRow={'left'}
      alignItems={'middle'}
      wrap={false}
    >
      {radioOption}
      <InputComponent
        {...componentProps}
        dataPoint={{name: '0', value: inputEmptyValue}}
        onChange={subInputOnChange}
        readOnly={!!readOnly}
        required={required}
        layout={{grow: 1}}
        width={!hasPrimary ? '90%' : '80%'}
      />
      {!readOnly && <Icon svg={'remove'}/>}
    </Row>;
  }

  const canAddInput = !(maxInputs && inputs.length === maxInputs);

  return <Column width={width} margin={margin || '5px'} layout={layout} alignItems={'left'}>
    {title && <TitleLabel title={title}/>}
    {inputs}
    {!readOnly && <Row
      height={'fit-content'}
      alignItems={'middle'}
      action={addInput}
      disabled={!canAddInput}
    >
      <_PlusIconWrapper>
        <Icon svg={'circlePlus'} disabled={!canAddInput}/>
      </_PlusIconWrapper>
    </Row>}
  </Column>;
};

MultiInput.propTypes = {
  /**
   * the input to display (all inputs are of the same type)
   * this must be the object returned from an import statement (i.e., pass Component from "import Component from ./Component.js")
   */
  InputComponent: PropTypes.object.isRequired,
  /**
   * the value is an array of data (not data points) corresponding to the inputs, bound to the parent
   * these will be added or removed with inputs
   * if hasPrimary is true, then value must be an array of objects, each with the 'primary' prop, and the second prop
   * contains the value for each input
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  /**
   * the function passed from parent to bind data points to parent, called when inputs are added, removed, or changed
   */
  onChange: PropTypes.func.isRequired,
  /**
   * an object containing all of the component props (except dataPoint and onChange, which should be omitted), with initial values
   * new inputs will be initialized with these props
   */
  componentProps: PropTypes.object.isRequired,
  /**
   * The prop name to pass to the sub input if the entire dataPoint value should not be passed.
   */
  dataPropName: PropTypes.string,
  /**
   * this must be an object mirroring the structure of the data as it exists on the parent,
   * with appropriate "empty" values for each prop (i.e., null, '', [], {}, etc.)
   * the appropriate prop names will be read from the object
   */
  emptyValue: PropTypes.object.isRequired,
  /**
   * the value used to initialize new inputs (this is passed separately from componentProps)
   * this must be an object mirroring the structure of the data as it exists on the parent,
   * with appropriate initial values for each prop
   * if omitted, new inputs will be initialized to the emptyValue prop
   */
  initialValue: PropTypes.object,
  /**
   * the width of the component
   */
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  /**
   * the margin of the component
   */
  margin: PropTypes.string,
  /**
   * if true, a radio input is displayed next to each input to select the primary input
   * the primary input is a required input
   * 'hasPrimary' and 'required' props cannot both be set to true
   */
  hasPrimary: (props, propName, componentName) => {
    PropTypes.checkPropTypes({[propName]: PropTypes.bool}, props, propName, componentName);

    if (props.hasPrimary && props.required) {
      return new Error(`${componentName} cannot have both 'hasPrimary' and 'required' set to true.`);
    }

    if (props.hasPrimary && props.dataPoint.value && props.dataPoint.value.length) {
      const missingPrimary = props.dataPoint.value.some(val => (
        (val === null)
        || (typeof val !== 'object')
        || ((typeof val === 'object') && !val.hasOwnProperty('primary'))
      ));

      if (missingPrimary) {
        return new Error(`If 'hasPrimary' prop of ${componentName} is set to true, then dataPoint.value must be an array
          of objects, each of which has at least the 'primary' prop.`);
      }
    }
  },
  /**
   * if true, then all inputs are required
   * 'hasPrimary' and 'required' props cannot both be set to true
   */
  required: (props, propName, componentName) => {
    PropTypes.checkPropTypes({[propName]: PropTypes.bool}, props, propName, componentName);
    if (props.hasPrimary && props.required) {
      return new Error(`${componentName} cannot have both 'hasPrimary' and 'required' set to true.`);
    }
  },
  /**
   * Title of the multi input.
   */
  title: PropTypes.string,
  /**
   * If true, strips the "title" prop from all inputs except for the first one.
   */
  displayHeaderTitle: PropTypes.bool,
  /**
   * if true, then all inputs are readOnly
   */
  readOnly: PropTypes.bool,
  /**
   * The maximum number of inputs to allow.
   */
  maxInputs: PropTypes.number,
  layout: layoutAttribute.propTypes,
};

export default withTheme(MultiInput);
